<template>
    <div class="attendance-worksite-drawer">
        <drawer-modal body-class="attendance-worksite-drawer__body" @close-modal="$emit('close')">
            <template slot="header">
                <div class="attendance-worksite-drawer__header-shell">
                    <div class="attendance-worksite-drawer__header-main">
                        <button
                            v-if="viewMode === 'detail'"
                            type="button"
                            class="btn btn-link attendance-worksite-drawer__back"
                            @click="goBackToEmployees"
                        >
                            <app-icon name="arrow-left" class="mr-2" />
                            {{ $t('back') }}
                        </button>
                        <div>
                            <h5 class="mb-1 attendance-worksite-drawer__header-title">{{ headerTitle }}</h5>
                            <p class="text-muted mb-0 attendance-worksite-drawer__header-subtitle">{{ headerSubtitle }}</p>
                        </div>
                    </div>
                    <button type="button" class="close outline-none" @click.prevent="$emit('close')">
                        <span><app-icon name="x"></app-icon></span>
                    </button>
                </div>
            </template>

            <template slot="body">
                <div class="position-relative attendance-worksite-drawer__canvas">
                    <app-overlay-loader v-if="isLoading || isDetailLoading" />

                    <div
                        v-if="viewMode === 'employees'"
                        class="attendance-worksite-drawer__content"
                        :class="{ 'loading-opacity': isLoading }"
                    >
                        <section class="card border-0 attendance-worksite-drawer__list-card">
                            <div class="card-body attendance-worksite-drawer__list-body">
                                <div class="attendance-worksite-drawer__list-context mb-2">
                                    <div class="attendance-worksite-drawer__list-context-main">
                                        <h6 class="mb-1">{{ locationName }}</h6>
                                        <p class="text-muted mb-0">
                                            {{ formatDateValue(selectedDate) }}
                                            <span v-if="locationCode && locationCode !== $t('not_available')">
                                                · {{ locationCode }}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                <div class="attendance-worksite-drawer__employee-toolbar mb-2">
                                    <div>
                                        <h6 class="mb-0">{{ $t('employees') }}</h6>
                                        <p class="attendance-worksite-drawer__employee-toolbar-hint mb-0">
                                            {{ $t('attendance_worksite_employee_hint') }}
                                        </p>
                                    </div>
                                    <span class="attendance-worksite-drawer__count-chip">
                                        {{ formatCountValue(recordsToRender.length) }} / {{ formatCountValue(summary.employees_count || 0) }}
                                    </span>
                                </div>

                                <div class="attendance-worksite-drawer__summary-pills attendance-worksite-drawer__summary-pills--compact mb-2">
                                    <span class="attendance-worksite-drawer__summary-pill is-present">
                                        {{ $t('present') }} · {{ formatCountValue(summary.present_count || 0) }}
                                    </span>
                                    <span class="attendance-worksite-drawer__summary-pill is-late">
                                        {{ $t('late') }} · {{ formatCountValue(summary.late_count || 0) }}
                                    </span>
                                    <span class="attendance-worksite-drawer__summary-pill is-absent">
                                        {{ $t('absent') }} · {{ formatCountValue(summary.absent_count || 0) }}
                                    </span>
                                    <span
                                        v-for="pill in summaryMetaPills"
                                        :key="pill.key"
                                        class="attendance-worksite-drawer__summary-pill"
                                        :class="pill.className"
                                    >
                                        {{ pill.label }} · {{ formatCountValue(pill.value) }}
                                    </span>
                                </div>

                                <div class="attendance-worksite-drawer__search-wrap mb-2">
                                    <span class="attendance-worksite-drawer__search-icon-wrap" aria-hidden="true">
                                        <app-icon name="search" class="attendance-worksite-drawer__search-icon" />
                                    </span>
                                    <input
                                        v-model.trim="employeeSearch"
                                        type="text"
                                        class="form-control attendance-worksite-drawer__search-input"
                                        :placeholder="$t('search')"
                                        :aria-label="$t('search')"
                                        autocomplete="off"
                                    />
                                </div>

                                <div v-if="!normalizedRecords.length" class="attendance-worksite-drawer__simple-empty">
                                    {{ $t('attendance_worksite_map_no_employees') }}
                                </div>

                                <div v-else-if="showSearchEmptyState" class="attendance-worksite-drawer__simple-empty">
                                    <span>{{ $t('attendance_worksite_map_no_employees_match') }}</span>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-secondary ml-2"
                                        @click="resetEmployeeSearch"
                                    >
                                        {{ $t('clear_filters') }}
                                    </button>
                                </div>

                                <ul v-else class="attendance-worksite-drawer__employee-plain-list">
                                    <li
                                        v-for="(record, index) in recordsToRender"
                                        :key="employeeRowKey(record, index)"
                                        class="attendance-worksite-drawer__employee-plain-item"
                                    >
                                        <button
                                            type="button"
                                            class="attendance-worksite-drawer__employee-plain-button"
                                            @click="openEmployeeDetail(record)"
                                        >
                                            <div class="attendance-worksite-drawer__employee-plain-main">
                                                <div class="attendance-worksite-drawer__employee-plain-identity">
                                                    <div class="attendance-worksite-drawer__avatar attendance-worksite-drawer__avatar--plain">
                                                        <img
                                                            :src="record.employee?.photo_url || getAppUrl('images/avatar-demo.jpg')"
                                                            :alt="record.uiEmployeeName"
                                                            loading="lazy"
                                                            decoding="async"
                                                        />
                                                    </div>
                                                    <div class="attendance-worksite-drawer__employee-plain-text">
                                                        <strong>{{ record.uiEmployeeName }}</strong>
                                                        <p class="mb-0 text-muted">
                                                            {{ record.uiMatricule }}
                                                            <span v-if="record.uiPhone && record.uiPhone !== '-'"> · {{ record.uiPhone }}</span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <div class="attendance-worksite-drawer__badges">
                                                    <span class="attendance-status-badge" :class="statusClass(record.status)">
                                                        {{ statusLabel(record.status) }}
                                                    </span>
                                                    <span v-if="record.status_flags?.late" class="attendance-status-badge attendance-status-badge--late">{{ $t('late') }}</span>
                                                    <span v-if="record.status_flags?.overtime" class="attendance-status-badge attendance-status-badge--overtime">{{ $t('overtime') }}</span>
                                                    <span v-if="record.status_flags?.suspicious" class="attendance-status-badge attendance-status-badge--suspicious">{{ $t('suspicious') }}</span>
                                                    <span v-if="record.day_context?.is_holiday" class="attendance-status-badge attendance-status-badge--holiday">{{ $t('holiday') }}</span>
                                                    <span v-else-if="record.day_context?.is_day_off" class="attendance-status-badge attendance-status-badge--day-off">{{ $t('day_off') }}</span>
                                                </div>
                                            </div>

                                            <div class="attendance-worksite-drawer__employee-plain-meta">
                                                <span>{{ formatDateTimeValue(record.first_check_in) || '-' }}</span>
                                                <span>{{ formatDateTimeValue(record.last_check_out) || '-' }}</span>
                                                <span>{{ formatDuration(record.total_hours) }}</span>
                                            </div>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </section>
                    </div>

                    <div
                        v-else-if="viewMode === 'detail' && timeline"
                        class="attendance-worksite-drawer__content"
                        :class="{ 'loading-opacity': isDetailLoading }"
                    >
                        <section class="attendance-worksite-drawer__summary card border-0 mb-3">
                            <div class="card-body">
                                <div class="attendance-worksite-drawer__summary-head attendance-worksite-drawer__summary-head--compact">
                                    <div class="attendance-worksite-drawer__identity">
                                        <div class="attendance-worksite-drawer__avatar attendance-worksite-drawer__avatar--large">
                                            <img
                                                :src="timeline.employee?.photo_url || getAppUrl('images/avatar-demo.jpg')"
                                                :alt="selectedEmployeeName"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </div>
                                        <div>
                                            <h6 class="mb-1">{{ selectedEmployeeName }}</h6>
                                            <p class="text-muted mb-0">
                                                {{ timeline.employee?.matricule || '-' }}
                                                <span v-if="selectedEmployeePhone && selectedEmployeePhone !== '-'"> · {{ selectedEmployeePhone }}</span>
                                            </p>
                                        </div>
                                    </div>

                                    <div class="attendance-worksite-drawer__badges">
                                        <span class="attendance-status-badge" :class="statusClass(timeline.status)">
                                            {{ statusLabel(timeline.status) }}
                                        </span>
                                        <span v-if="timeline.day_context?.is_holiday" class="attendance-status-badge attendance-status-badge--holiday">{{ $t('holiday') }}</span>
                                        <span v-else-if="timeline.day_context?.is_day_off" class="attendance-status-badge attendance-status-badge--day-off">{{ $t('day_off') }}</span>
                                    </div>
                                </div>

                                <div v-if="timeline.day_context?.holiday" class="attendance-worksite-drawer__holiday-note mt-3">
                                    <app-icon name="calendar" class="mr-2" />
                                    <div>
                                        <strong>{{ timeline.day_context.holiday.name }}</strong>
                                        <p class="mb-0 text-muted">{{ timeline.day_context.holiday.description || $t('holiday') }}</p>
                                    </div>
                                </div>

                                <div class="attendance-worksite-drawer__detail-facts mt-3">
                                    <div class="attendance-worksite-drawer__detail-fact">
                                        <span class="attendance-worksite-drawer__detail-label">{{ $t('clock_in') }}</span>
                                        <strong class="attendance-worksite-drawer__detail-value">{{ formatDateTimeValue(timeline.clock_in) || '-' }}</strong>
                                    </div>
                                    <div class="attendance-worksite-drawer__detail-fact">
                                        <span class="attendance-worksite-drawer__detail-label">{{ $t('clock_out') }}</span>
                                        <strong class="attendance-worksite-drawer__detail-value">{{ formatDateTimeValue(timeline.clock_out) || '-' }}</strong>
                                    </div>
                                    <div class="attendance-worksite-drawer__detail-fact">
                                        <span class="attendance-worksite-drawer__detail-label">{{ $t('duration') }}</span>
                                        <strong class="attendance-worksite-drawer__detail-value">{{ formatDuration(timeline.total_hours) }}</strong>
                                    </div>
                                    <div class="attendance-worksite-drawer__detail-fact">
                                        <span class="attendance-worksite-drawer__detail-label">{{ $t('shift') }}</span>
                                        <strong class="attendance-worksite-drawer__detail-value">{{ timeline.shift?.name || '-' }}</strong>
                                    </div>
                                    <div class="attendance-worksite-drawer__detail-fact attendance-worksite-drawer__detail-fact--wide">
                                        <span class="attendance-worksite-drawer__detail-label">{{ $t('location') }}</span>
                                        <strong class="attendance-worksite-drawer__detail-value">{{ timeline.location?.name || '-' }}</strong>
                                    </div>
                                    <div class="attendance-worksite-drawer__detail-fact attendance-worksite-drawer__detail-fact--wide">
                                        <span class="attendance-worksite-drawer__detail-label">{{ $t('device') }}</span>
                                        <strong class="attendance-worksite-drawer__detail-value">{{ primaryDevice }}</strong>
                                    </div>
                                    <div class="attendance-worksite-drawer__detail-fact attendance-worksite-drawer__detail-fact--wide">
                                        <span class="attendance-worksite-drawer__detail-label">{{ $t('phone') }}</span>
                                        <a
                                            v-if="selectedEmployeePhone && selectedEmployeePhone !== '-'"
                                            :href="phoneHref(selectedEmployeePhone)"
                                            class="attendance-worksite-drawer__phone-link"
                                        >
                                            <app-icon name="phone" class="attendance-worksite-drawer__phone-link-icon" />
                                            <span>{{ selectedEmployeePhone }}</span>
                                        </a>
                                        <strong v-else class="attendance-worksite-drawer__detail-value">-</strong>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="card border-0 mb-4 attendance-worksite-drawer__timeline-card">
                            <div class="card-body">
                                <div class="attendance-section-head mb-3">
                                    <div>
                                        <h6 class="mb-1">{{ $t('activity_timeline') }}</h6>
                                        <p class="text-muted mb-0">{{ $t('attendance_timeline_drawer_hint') }}</p>
                                    </div>
                                </div>

                                <div class="attendance-worksite-drawer__timeline-list">
                                    <div
                                        v-for="item in timelineSteps"
                                        :key="item.key"
                                        class="attendance-timeline-step"
                                        :class="{ 'is-missing': !item.value }"
                                    >
                                        <div class="attendance-timeline-step__icon">
                                            <app-icon :name="item.icon" />
                                        </div>
                                        <div class="attendance-timeline-step__content">
                                            <div class="d-flex justify-content-between align-items-start flex-wrap">
                                                <div>
                                                    <strong>{{ item.label }}</strong>
                                                    <p class="mb-0 text-muted">{{ item.help }}</p>
                                                </div>
                                                <span class="attendance-timeline-step__value">{{ item.value || '-' }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section v-if="timeline.anomalies && timeline.anomalies.length" class="card border-0 mb-4">
                            <div class="card-body">
                                <div class="attendance-section-head mb-3">
                                    <div>
                                        <h6 class="mb-1">{{ $t('attendance_anomalies') }}</h6>
                                        <p class="text-muted mb-0">{{ $t('attendance_anomalies_hint') }}</p>
                                    </div>
                                </div>
                                <div class="attendance-anomaly-list">
                                    <div v-for="anomaly in timeline.anomalies" :key="anomaly.code + anomaly.message" class="attendance-anomaly-item">
                                        <app-icon name="alert-triangle" class="mr-2" />
                                        <div>
                                            <strong>{{ anomalyLabel(anomaly.code) }}</strong>
                                            <p class="mb-0 text-muted">{{ anomaly.message }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="card border-0 mb-4">
                            <div class="card-body">
                                <div class="attendance-section-head mb-3">
                                    <div>
                                        <h6 class="mb-1">{{ $t('google_maps') }}</h6>
                                        <p class="text-muted mb-0">{{ $t('attendance_map_hint') }}</p>
                                    </div>
                                </div>

                                <div v-if="mapUnavailableMessage" class="attendance-map-state attendance-map-state--empty">
                                    <app-icon name="map-pin" />
                                    <p class="mb-0">{{ mapUnavailableMessage }}</p>
                                </div>

                                <div v-else>
                                    <div ref="map" class="attendance-worksite-drawer__map"></div>
                                    <div class="attendance-map-legend mt-3">
                                        <span class="attendance-map-legend__item">
                                            <span class="attendance-map-legend__swatch attendance-map-legend__swatch--clock"></span>
                                            {{ $t('clock_in') }}
                                        </span>
                                        <span v-if="timeline.location" class="attendance-map-legend__item">
                                            <span class="attendance-map-legend__swatch attendance-map-legend__swatch--worksite"></span>
                                            {{ $t('location') }}
                                        </span>
                                        <span v-if="hasGeofence" class="attendance-map-legend__item">
                                            <span class="attendance-map-legend__swatch attendance-map-legend__swatch--geofence"></span>
                                            {{ $t('geofence') }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section v-if="timeline.events && timeline.events.length" class="card border-0 mb-4">
                            <div class="card-body">
                                <div class="attendance-section-head mb-3">
                                    <div>
                                        <h6 class="mb-1">{{ $t('attendance_event_log') }}</h6>
                                        <p class="text-muted mb-0">{{ $t('attendance_event_log_hint') }}</p>
                                    </div>
                                </div>

                                <div class="attendance-event-list">
                                    <div v-for="event in timeline.events" :key="event.id" class="attendance-event-item">
                                        <div class="attendance-event-item__head">
                                            <span class="attendance-status-badge" :class="event.type === 'in' ? 'attendance-status-badge--ontime' : 'attendance-status-badge--neutral'">
                                                {{ event.type === 'in' ? $t('clock_in') : $t('clock_out') }}
                                            </span>
                                            <strong>{{ formatDateTimeValue(event.time) }}</strong>
                                        </div>
                                        <div class="attendance-event-item__meta">
                                            <span>{{ event.location?.name || '-' }}</span>
                                            <span>{{ event.device?.identifier || '-' }}</span>
                                            <span :class="event.gps?.within_geofence ? 'text-success' : 'text-warning'">
                                                {{ event.gps?.within_geofence ? $t('is_within_geofence') : $t('outside_geofence_short') }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </template>
        </drawer-modal>
    </div>
</template>

<script>
import DrawerModal from '../../Helpers/Modal/DrawerModal.vue';
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { ATTENDANCE_TABLE, ATTENDANCE_TIMELINE } from '../../../Config/ApiUrl';

const FALLBACK_GOOGLE_MAPS_KEY = 'AIzaSyA8emh2Pq3wIootXyoX5zILgIqaxxOrs-E';

export default {
    name: 'AttendanceWorksiteDrawer',
    extends: CoreLibrary,
    components: { DrawerModal },
    props: {
        locationSummary: {
            type: Object,
            required: true,
        },
        selectedDate: {
            type: String,
            required: true,
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            isLoading: false,
            isDetailLoading: false,
            viewMode: 'employees',
            records: [],
            responseSummary: null,
            usedFallbackEmployees: false,
            employeeSearch: '',
            selectedRecord: null,
            timeline: null,
            map: null,
            mapMarker: null,
            worksiteMarker: null,
            geofenceOverlay: null,
            mapError: false,
        };
    },
    computed: {
        summary() {
            return this.responseSummary || this.locationSummary?.summary || {};
        },
        location() {
            return this.locationSummary?.location || {};
        },
        locationName() {
            return this.location.name || this.$t('location');
        },
        locationCode() {
            return this.location.code || this.$t('not_available');
        },
        locationAddress() {
            return this.location.address || this.location.description || this.$t('attendance_worksite_map_no_address');
        },
        locationTone() {
            return this.locationSummary?.tone || 'neutral';
        },
        normalizedRecords() {
            return (Array.isArray(this.records) ? this.records : [])
                .map((record, index) => this.normalizeRecord(record, index))
                .filter(Boolean)
                .sort((left, right) => {
                    const severity = this.statusPriority(left) - this.statusPriority(right);
                    if (severity !== 0) {
                        return severity;
                    }

                    const leftName = left.uiEmployeeName || '';
                    const rightName = right.uiEmployeeName || '';
                    return leftName.localeCompare(rightName);
                });
        },
        hasSearchQuery() {
            return Boolean((this.employeeSearch || '').trim());
        },
        filteredRecords() {
            const query = (this.employeeSearch || '').trim().toLowerCase();
            if (!query) {
                return this.normalizedRecords;
            }

            return this.normalizedRecords.filter((record) => {
                return [
                    record.uiEmployeeName,
                    record.uiMatricule,
                    record.status,
                    ...(record.device_info?.devices || []),
                ]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(query));
            });
        },
        recordsToRender() {
            return this.hasSearchQuery ? this.filteredRecords : this.normalizedRecords;
        },
        showSearchEmptyState() {
            return this.hasSearchQuery && !this.filteredRecords.length;
        },
        headerTitle() {
            if (this.viewMode === 'detail') {
                return this.selectedEmployeeName;
            }

            return this.locationName;
        },
        headerSubtitle() {
            if (this.viewMode === 'detail') {
                return this.formatDateValue(this.timeline?.date || this.selectedDate);
            }

            return this.$t('attendance_worksite_drawer_subtitle')
                .replace(':count', this.formatCountValue(this.summary.employees_count || 0))
                .replace(':date', this.formatDateValue(this.selectedDate));
        },
        selectedEmployeeName() {
            return this.timeline?.employee?.full_name || this.selectedRecord?.employee?.full_name || '-';
        },
        selectedEmployeePhone() {
            return this.timeline?.employee?.phone
                || this.selectedRecord?.employee?.phone
                || this.selectedRecord?.phone
                || '-';
        },
        primaryDevice() {
            return this.timeline?.device_info?.devices?.[0] || this.$t('not_available');
        },
        mapFocusPoint() {
            const clockInGps = (this.timeline?.gps_points || []).find((point) => point.type === 'in');
            return clockInGps || this.timeline?.gps_points?.[0] || null;
        },
        hasGeofence() {
            const location = this.timeline?.location;
            if (!location) {
                return false;
            }

            return Boolean(
                (location.geofence_type === 'polygon' && location.geojson)
                || ((location.geofence_type === 'radius' || location.radius || location.radius_m) && location.latitude && location.longitude)
            );
        },
        mapUnavailableMessage() {
            if (this.mapError) {
                return this.$t('google_maps_failed_to_load');
            }

            if (!this.mapFocusPoint && !this.timeline?.location?.latitude) {
                return this.$t('attendance_map_unavailable');
            }

            return '';
        },
        summaryMetaPills() {
            return [
                {
                    key: 'suspicious',
                    label: this.$t('suspicious'),
                    value: this.summary.suspicious_count || 0,
                    className: 'is-suspicious',
                },
                {
                    key: 'holiday',
                    label: this.$t('holiday'),
                    value: this.summary.holiday_count || 0,
                    className: 'is-holiday',
                },
                {
                    key: 'day_off',
                    label: this.$t('day_off'),
                    value: this.summary.day_off_count || 0,
                    className: 'is-day-off',
                },
            ].filter((pill) => Number(pill.value || 0) > 0);
        },
        timelineSteps() {
            return [
                {
                    key: 'clock_in',
                    label: this.$t('clock_in'),
                    value: this.formatDateTimeValue(this.timeline?.clock_in),
                    help: this.$t('attendance_clock_in_help'),
                    icon: 'log-in',
                },
                {
                    key: 'break_start',
                    label: this.$t('break_start'),
                    value: this.formatDateTimeValue(this.timeline?.break_start),
                    help: this.$t('attendance_break_start_help'),
                    icon: 'coffee',
                },
                {
                    key: 'break_end',
                    label: this.$t('break_end'),
                    value: this.formatDateTimeValue(this.timeline?.break_end),
                    help: this.$t('attendance_break_end_help'),
                    icon: 'play-circle',
                },
                {
                    key: 'clock_out',
                    label: this.$t('clock_out'),
                    value: this.formatDateTimeValue(this.timeline?.clock_out),
                    help: this.$t('attendance_clock_out_help'),
                    icon: 'log-out',
                },
            ];
        },
        googleMapsApiKey() {
            return process.env.MIX_GOOGLE_MAPS_API_KEY || FALLBACK_GOOGLE_MAPS_KEY;
        },
    },
    mounted() {
        this.loadLocationPresence();
    },
    beforeDestroy() {
        this.clearMapOverlays();
    },
    methods: {
        async fetchLocationPresence(params) {
            return this.fetchFromAttendanceTable(params);
        },
        async fetchFromAttendanceTable(params) {
            const response = await this.axiosGet(ATTENDANCE_TABLE, { params });
            const payload = response.data || {};
            const employees = payload.data || [];
            const pagination = payload.meta?.pagination || {};
            const total = Number(pagination.total || employees.length || 0);

            return {
                summary: {
                    employees_count: total,
                    present_count: Number(payload.meta?.total_present || 0),
                    absent_count: Number(payload.meta?.total_absent || 0),
                    late_count: Number(payload.meta?.total_late || 0),
                    overtime_count: Number(payload.meta?.total_overtime || 0),
                    suspicious_count: Number(payload.meta?.total_suspicious || 0),
                    holiday_count: employees.filter((record) => record?.day_context?.is_holiday).length,
                    day_off_count: employees.filter((record) => !record?.day_context?.is_holiday && record?.day_context?.is_day_off).length,
                },
                employees,
            };
        },
        statusPriority(record) {
            if (record.status_flags?.suspicious) {
                return 0;
            }
            if (record.status_flags?.absent) {
                return 1;
            }
            if (record.status_flags?.late) {
                return 2;
            }
            if (record.day_context?.is_holiday) {
                return 3;
            }
            if (record.day_context?.is_day_off) {
                return 4;
            }
            if (record.status_flags?.overtime) {
                return 5;
            }
            return 6;
        },
        toneLabel(tone) {
            const labels = {
                critical: this.$t('attention_required'),
                warning: this.$t('monitoring'),
                healthy: this.$t('healthy'),
                neutral: this.$t('steady'),
            };

            return labels[tone] || labels.neutral;
        },
        async loadLocationPresence() {
            this.isLoading = true;
            this.usedFallbackEmployees = false;

            try {
                const params = this.buildRequestParams();
                let payload = await this.fetchLocationPresence(params);

                if (!payload.employees.length) {
                    payload = await this.fetchLocationPresence({
                        date_range: `${this.selectedDate},${this.selectedDate}`,
                        location_id: this.location.id,
                        per_page: 500,
                        sort: 'employee_name:asc',
                    });
                    this.usedFallbackEmployees = true;
                }

                this.responseSummary = payload.summary || this.responseSummary;
                this.records = payload.employees;
            } catch (error) {
                this.$toastr.e(this.$t('error_loading_attendance_listing'));
            } finally {
                this.isLoading = false;
            }
        },
        buildRequestParams() {
            const params = {
                date_range: `${this.selectedDate},${this.selectedDate}`,
                location_id: this.location.id,
                per_page: 500,
                sort: 'employee_name:asc',
            };

            [
                'employee_id',
                'shift_id',
                'department_id',
                'status',
            ].forEach((key) => {
                if (this.filters[key]) {
                    params[key] = this.filters[key];
                }
            });

            return params;
        },
        normalizeRecord(record, index = 0) {
            if (record === null || typeof record === 'undefined') {
                return null;
            }

            const safeRecord = typeof record === 'object' ? record : { raw_value: record };

            return {
                ...safeRecord,
                employee_id: safeRecord.employee_id || safeRecord.employee?.id || `worksite-employee-${index}`,
                date: safeRecord.date || this.selectedDate,
                uiEmployeeName: safeRecord.employee?.full_name
                    || safeRecord.employee?.name
                    || safeRecord.employee_name
                    || safeRecord.full_name
                    || safeRecord.name
                    || String(safeRecord.raw_value || this.$t('employee')),
                uiMatricule: safeRecord.employee?.matricule
                    || safeRecord.matricule
                    || '-',
                uiPhone: safeRecord.employee?.phone
                    || safeRecord.phone
                    || '-',
            };
        },
        employeeRowKey(record, index) {
            return `${record.employee_id || 'unknown'}-${record.date || this.selectedDate}-${index}`;
        },
        resetEmployeeSearch() {
            this.employeeSearch = '';
        },
        async openEmployeeDetail(record) {
            this.selectedRecord = record;
            this.viewMode = 'detail';
            this.isDetailLoading = true;
            this.mapError = false;
            this.timeline = null;

            try {
                const url = record.timeline_url
                    ? record.timeline_url.replace(this.getBaseUrl(), '').replace(/^\//, '')
                    : `${ATTENDANCE_TIMELINE}/${record.employee_id}/timeline?date=${record.date}`;
                const response = await this.axiosGet(url);
                this.timeline = response.data;
                this.$nextTick(async () => {
                    try {
                        await this.renderMap();
                    } catch (error) {
                        this.mapError = true;
                    }
                });
            } catch (error) {
                this.$toastr.e(this.$t('error_loading_attendance_timeline'));
            } finally {
                this.isDetailLoading = false;
            }
        },
        goBackToEmployees() {
            this.viewMode = 'employees';
            this.selectedRecord = null;
            this.timeline = null;
            this.mapError = false;
            this.clearMapOverlays();
        },
        formatDateValue(value) {
            if (!value) {
                return '-';
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return new Intl.DateTimeFormat(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            }).format(date);
        },
        formatDateTimeValue(value) {
            if (!value) {
                return null;
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return new Intl.DateTimeFormat(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            }).format(date);
        },
        formatDuration(value) {
            const totalHours = Number(value || 0);
            if (!Number.isFinite(totalHours) || totalHours <= 0) {
                return '-';
            }

            const totalMinutes = Math.round(totalHours * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
        },
        phoneHref(phone) {
            const normalized = String(phone || '').replace(/[^\d+]/g, '');
            return normalized ? `tel:${normalized}` : '#';
        },
        statusLabel(status) {
            return this.$t(status || 'untracked');
        },
        statusClass(status) {
            const classes = {
                on_time: 'attendance-status-badge--ontime',
                present: 'attendance-status-badge--present',
                late: 'attendance-status-badge--late',
                absent: 'attendance-status-badge--absent',
                overtime: 'attendance-status-badge--overtime',
                suspicious: 'attendance-status-badge--suspicious',
                untracked: 'attendance-status-badge--neutral',
            };

            return classes[status] || classes.untracked;
        },
        anomalyLabel(code) {
            const labels = {
                missing_clock_out: this.$t('missing_clock_out'),
                incomplete_break: this.$t('incomplete_break'),
                outside_geofence: this.$t('outside_geofence'),
                location_mismatch: this.$t('location_mismatch'),
                attendance_on_day_off: this.$t('attendance_on_day_off'),
                multiple_devices: this.$t('multiple_devices'),
            };

            return labels[code] || code;
        },
        async loadGoogleMaps() {
            if (window.google && window.google.maps) {
                return window.google.maps;
            }

            if (window.__mozaliGoogleMapsPromise) {
                return window.__mozaliGoogleMapsPromise;
            }

            window.__mozaliGoogleMapsPromise = new Promise((resolve, reject) => {
                const existingScript = document.getElementById('mozali-google-maps');

                if (existingScript) {
                    existingScript.addEventListener('load', () => resolve(window.google.maps));
                    existingScript.addEventListener('error', reject);
                    return;
                }

                const script = document.createElement('script');
                script.id = 'mozali-google-maps';
                script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=geometry`;
                script.async = true;
                script.defer = true;
                script.onload = () => resolve(window.google.maps);
                script.onerror = reject;
                document.head.appendChild(script);
            });

            return window.__mozaliGoogleMapsPromise;
        },
        async renderMap() {
            if (this.mapUnavailableMessage || !this.$refs.map) {
                return;
            }

            await this.loadGoogleMaps();
            this.clearMapOverlays();

            const fallbackCenter = {
                lat: Number(this.timeline?.location?.latitude || this.mapFocusPoint?.latitude || -11.6645),
                lng: Number(this.timeline?.location?.longitude || this.mapFocusPoint?.longitude || 27.4793),
            };

            this.map = new window.google.maps.Map(this.$refs.map, {
                center: fallbackCenter,
                zoom: 15,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            });

            const bounds = new window.google.maps.LatLngBounds();

            if (this.timeline?.location?.latitude && this.timeline?.location?.longitude) {
                const worksitePosition = {
                    lat: Number(this.timeline.location.latitude),
                    lng: Number(this.timeline.location.longitude),
                };

                this.worksiteMarker = new window.google.maps.Marker({
                    map: this.map,
                    position: worksitePosition,
                    title: this.timeline.location.name || this.$t('location'),
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 9,
                        fillColor: '#2563eb',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                    },
                });
                bounds.extend(worksitePosition);
            }

            if (this.hasGeofence) {
                this.renderGeofence(bounds);
            }

            if (this.mapFocusPoint?.latitude && this.mapFocusPoint?.longitude) {
                const clockInPosition = {
                    lat: Number(this.mapFocusPoint.latitude),
                    lng: Number(this.mapFocusPoint.longitude),
                };

                this.mapMarker = new window.google.maps.Marker({
                    map: this.map,
                    position: clockInPosition,
                    title: this.$t('clock_in'),
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: '#10b981',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                    },
                });
                bounds.extend(clockInPosition);
            }

            if (!bounds.isEmpty()) {
                this.map.fitBounds(bounds, 64);
            }
        },
        renderGeofence(bounds) {
            const location = this.timeline?.location;
            if (!location) {
                return;
            }

            if (location.geofence_type === 'polygon' && location.geojson) {
                const path = this.geoJsonToPath(location.geojson);
                if (!path.length) {
                    return;
                }

                this.geofenceOverlay = new window.google.maps.Polygon({
                    map: this.map,
                    paths: path,
                    strokeColor: '#7c3aed',
                    strokeOpacity: 0.9,
                    strokeWeight: 2,
                    fillColor: '#7c3aed',
                    fillOpacity: 0.12,
                });

                path.forEach((point) => bounds.extend(point));
                return;
            }

            if (location.latitude && location.longitude && (location.radius_m || location.radius)) {
                this.geofenceOverlay = new window.google.maps.Circle({
                    map: this.map,
                    center: {
                        lat: Number(location.latitude),
                        lng: Number(location.longitude),
                    },
                    radius: Number(location.radius_m || location.radius),
                    strokeColor: '#7c3aed',
                    strokeOpacity: 0.85,
                    strokeWeight: 2,
                    fillColor: '#7c3aed',
                    fillOpacity: 0.1,
                });

                const circleBounds = this.geofenceOverlay.getBounds();
                if (circleBounds) {
                    bounds.union(circleBounds);
                }
            }
        },
        geoJsonToPath(geojson) {
            if (!geojson || !window.google?.maps) {
                return [];
            }

            const geometry = geojson.type === 'Feature' ? geojson.geometry : geojson;
            if (!geometry || geometry.type !== 'Polygon') {
                return [];
            }

            const ring = geometry.coordinates?.[0] || [];
            return ring
                .filter((point) => Array.isArray(point) && point.length >= 2)
                .map((point) => ({ lat: Number(point[1]), lng: Number(point[0]) }))
                .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
        },
        clearMapOverlays() {
            if (this.mapMarker) {
                this.mapMarker.setMap(null);
                this.mapMarker = null;
            }

            if (this.worksiteMarker) {
                this.worksiteMarker.setMap(null);
                this.worksiteMarker = null;
            }

            if (this.geofenceOverlay) {
                this.geofenceOverlay.setMap(null);
                this.geofenceOverlay = null;
            }
        },
    },
};
</script>

<style scoped>
.attendance-worksite-drawer >>> .app-drawer-modal__panel {
    width: min(680px, 100%);
}

.attendance-worksite-drawer__body {
    padding: 1.25rem;
    background: #f8fafc;
}

.attendance-worksite-drawer__canvas {
    min-height: 14rem;
}

.attendance-worksite-drawer__header-shell,
.attendance-worksite-drawer__worksite-head,
.attendance-worksite-drawer__summary-head,
.attendance-worksite-drawer__employee-main,
.attendance-worksite-drawer__employee-meta,
.attendance-event-item__head,
.attendance-event-item__meta,
.attendance-worksite-drawer__title-row,
.attendance-worksite-drawer__identity,
.attendance-worksite-drawer__header-main,
.attendance-map-legend {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.attendance-worksite-drawer__header-shell,
.attendance-worksite-drawer__list-card,
.attendance-worksite-drawer__summary,
.attendance-worksite-drawer__timeline-card,
.attendance-worksite-drawer__content .card {
    border-radius: 1rem;
    box-shadow: none;
    background: #ffffff;
}

.attendance-worksite-drawer__list-card,
.attendance-worksite-drawer__summary,
.attendance-worksite-drawer__timeline-card,
.attendance-worksite-drawer__content .card {
    border: 1px solid rgba(148, 163, 184, 0.14);
}

.attendance-worksite-drawer__header-shell {
    padding: 0.1rem 0 0.35rem;
    border: 0;
    background: transparent;
}

.attendance-worksite-drawer__header-main,
.attendance-worksite-drawer__summary-head,
.attendance-worksite-drawer__employee-main,
.attendance-worksite-drawer__employee-meta,
.attendance-event-item__meta,
.attendance-map-legend,
.attendance-worksite-drawer__title-row {
    flex-wrap: wrap;
}

.attendance-worksite-drawer__back {
    padding: 0;
    font-weight: 700;
}

.attendance-worksite-drawer__header-title {
    font-size: 1.45rem;
    font-weight: 700;
    color: #0f172a;
}

.attendance-worksite-drawer__header-subtitle {
    font-size: 0.92rem;
    color: #64748b !important;
}

.attendance-worksite-drawer__list-body {
    padding: 1rem;
}

.attendance-worksite-drawer__list-context {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.attendance-worksite-drawer__list-context-main h6 {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
}

.attendance-worksite-drawer__list-context-main p {
    font-size: 0.86rem;
}

.attendance-worksite-drawer__employee-toolbar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem;
}

.attendance-worksite-drawer__employee-toolbar-hint {
    margin-top: 0.2rem;
    font-size: 0.82rem;
    color: #94a3b8;
}

.attendance-worksite-drawer__list-head {
    align-items: flex-start;
}

.attendance-worksite-drawer__count-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.45rem 0.8rem;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
}

.attendance-worksite-drawer__tone,
.attendance-worksite-drawer__worksite-chip,
.attendance-status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    padding: 0.45rem 0.8rem;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1;
}

.attendance-worksite-drawer__tone.is-critical {
    background: rgba(239, 68, 68, 0.14);
    color: #b91c1c;
}

.attendance-worksite-drawer__tone.is-warning {
    background: rgba(245, 158, 11, 0.16);
    color: #b45309;
}

.attendance-worksite-drawer__tone.is-healthy {
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
}

.attendance-worksite-drawer__tone.is-neutral,
.attendance-worksite-drawer__worksite-chip,
.attendance-status-badge--neutral,
.attendance-status-badge--day-off {
    background: rgba(100, 116, 139, 0.14);
    color: #475569;
}

.attendance-status-badge--ontime,
.attendance-status-badge--present {
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
}

.attendance-status-badge--late {
    background: rgba(245, 158, 11, 0.16);
    color: #b45309;
}

.attendance-status-badge--absent {
    background: rgba(239, 68, 68, 0.14);
    color: #b91c1c;
}

.attendance-status-badge--overtime {
    background: rgba(59, 130, 246, 0.14);
    color: #1d4ed8;
}

.attendance-status-badge--suspicious {
    background: rgba(168, 85, 247, 0.15);
    color: #7e22ce;
}

.attendance-status-badge--holiday {
    background: rgba(14, 165, 233, 0.14);
    color: #0369a1;
}

.attendance-worksite-drawer__summary-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
}

.attendance-worksite-drawer__summary-pills--compact {
    gap: 0.45rem;
}

.attendance-worksite-drawer__summary-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.45rem 0.8rem;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1;
}

.attendance-worksite-drawer__summary-pills--compact .attendance-worksite-drawer__summary-pill {
    padding: 0.34rem 0.65rem;
    font-size: 0.73rem;
}

.attendance-worksite-drawer__summary-pill.is-present {
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
}

.attendance-worksite-drawer__summary-pill.is-late {
    background: rgba(245, 158, 11, 0.16);
    color: #b45309;
}

.attendance-worksite-drawer__summary-pill.is-absent {
    background: rgba(239, 68, 68, 0.14);
    color: #b91c1c;
}

.attendance-worksite-drawer__summary-pill.is-suspicious {
    background: rgba(168, 85, 247, 0.15);
    color: #7e22ce;
}

.attendance-worksite-drawer__summary-pill.is-holiday {
    background: rgba(14, 165, 233, 0.14);
    color: #0369a1;
}

.attendance-worksite-drawer__summary-pill.is-day-off {
    background: rgba(100, 116, 139, 0.14);
    color: #475569;
}

.attendance-worksite-drawer__search-wrap {
    display: flex;
    align-items: center;
    min-width: 100%;
    padding: 0 1rem;
    border: 1px solid #d9e2ec;
    border-radius: 999px;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.attendance-worksite-drawer__search-wrap:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.12);
}

.attendance-worksite-drawer__search-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1rem;
    width: 1rem;
    min-width: 1rem;
    margin-right: 0.75rem;
    color: #94a3b8;
}

.attendance-worksite-drawer__search-input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0 !important;
    border-radius: 0 !important;
    padding: 0.85rem 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
}

.attendance-worksite-drawer__search-input:focus {
    box-shadow: none !important;
}

.attendance-worksite-drawer__search-input::placeholder {
    color: #94a3b8;
}

.attendance-worksite-drawer__search-icon {
    width: 1rem;
    height: 1rem;
    pointer-events: none;
}

.attendance-worksite-drawer__loaded-count {
    font-size: 0.82rem;
    font-weight: 700;
    color: #475569;
}

.attendance-worksite-drawer__simple-empty {
    padding: 0.95rem 0;
    color: #64748b;
    font-size: 0.95rem;
}

.attendance-worksite-drawer__employee-plain-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.attendance-worksite-drawer__employee-plain-item + .attendance-worksite-drawer__employee-plain-item {
    margin-top: 0.7rem;
}

.attendance-worksite-drawer__employee-plain-button {
    display: block;
    width: 100%;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 1rem;
    background: #ffffff;
    padding: 0.9rem 1rem;
    text-align: left;
    transition: border-color 0.18s ease, background-color 0.18s ease;
}

.attendance-worksite-drawer__employee-plain-button:hover {
    border-color: rgba(37, 99, 235, 0.24);
    background: #f8fbff;
}

.attendance-worksite-drawer__employee-plain-main,
.attendance-worksite-drawer__employee-plain-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.attendance-worksite-drawer__employee-plain-meta {
    margin-top: 0.65rem;
    color: #64748b;
    font-size: 0.88rem;
}

.attendance-worksite-drawer__employee-plain-identity {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    min-width: 0;
}

.attendance-worksite-drawer__employee-plain-text {
    min-width: 0;
}

.attendance-worksite-drawer__employee-plain-text strong,
.attendance-worksite-drawer__employee-plain-text p {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attendance-worksite-drawer__avatar--plain {
    width: 3.5rem;
    height: 3.5rem;
}

.attendance-worksite-drawer__employee-list {
    display: block;
    overflow: visible;
    max-height: none;
    padding-right: 0;
}

.attendance-worksite-drawer__employee-item {
    display: block;
    width: 100%;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 1rem;
    background: #fff;
    padding: 1rem;
    text-align: left;
    transition: border-color 0.18s ease, background-color 0.18s ease;
    margin-bottom: 0.85rem;
}

.attendance-worksite-drawer__employee-item:hover {
    border-color: rgba(37, 99, 235, 0.24);
    background: #f8fbff;
}

.attendance-worksite-drawer__employee-meta {
    margin-top: 0.9rem;
    color: #64748b;
    font-size: 0.88rem;
}

.attendance-worksite-drawer__employee-action {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 0.85rem;
    color: #94a3b8;
}

.attendance-worksite-drawer__identity,
.attendance-worksite-drawer__employee-identity {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.attendance-worksite-drawer__avatar {
    width: 3rem;
    height: 3rem;
    border-radius: 999px;
    overflow: hidden;
    background: #e2e8f0;
    flex-shrink: 0;
}

.attendance-worksite-drawer__avatar--large {
    width: 5.25rem;
    height: 5.25rem;
}

.attendance-worksite-drawer__summary-head--compact {
    align-items: flex-start;
}

.attendance-worksite-drawer__detail-facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.attendance-worksite-drawer__detail-fact {
    padding: 0.9rem 1rem;
    border-radius: 0.95rem;
    background: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.14);
}

.attendance-worksite-drawer__detail-fact--wide {
    grid-column: 1 / -1;
}

.attendance-worksite-drawer__detail-label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
}

.attendance-worksite-drawer__detail-value {
    display: block;
    font-size: 0.98rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.45;
}

.attendance-worksite-drawer__phone-link {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    text-decoration: none;
    line-height: 1.4;
}

.attendance-worksite-drawer__phone-link:hover {
    color: #2563eb;
    text-decoration: none;
}

.attendance-worksite-drawer__phone-link-icon {
    width: 1rem;
    height: 1rem;
    color: #2563eb;
    flex-shrink: 0;
}

.attendance-worksite-drawer__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.attendance-worksite-drawer__holiday-note,
.attendance-anomaly-item,
.attendance-worksite-drawer__empty-state,
.attendance-map-state {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.95rem 1rem;
    border-radius: 0.95rem;
    background: #f8fafc;
}

.attendance-worksite-drawer__empty-state {
    border: 1px dashed rgba(148, 163, 184, 0.3);
    justify-content: center;
    text-align: center;
}

.attendance-worksite-drawer__timeline-list,
.attendance-anomaly-list,
.attendance-event-list {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.attendance-timeline-step {
    display: flex;
    align-items: stretch;
    gap: 0.9rem;
    padding: 0.95rem 1rem;
    border-radius: 1rem;
    background: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.15);
}

.attendance-timeline-step.is-missing {
    opacity: 0.7;
}

.attendance-timeline-step__icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(37, 99, 235, 0.12);
    color: #1d4ed8;
    flex-shrink: 0;
}

.attendance-timeline-step__content {
    flex: 1 1 auto;
}

.attendance-timeline-step__value {
    font-weight: 700;
    color: #0f172a;
}

.attendance-worksite-drawer__map {
    width: 100%;
    min-height: 280px;
    border-radius: 1rem;
    overflow: hidden;
    background: #e2e8f0;
}

.attendance-map-legend {
    justify-content: flex-start;
}

.attendance-map-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.82rem;
    color: #475569;
}

.attendance-map-legend__swatch {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 999px;
}

.attendance-map-legend__swatch--clock {
    background: #10b981;
}

.attendance-map-legend__swatch--worksite {
    background: #2563eb;
}

.attendance-map-legend__swatch--geofence {
    background: #7c3aed;
}

.attendance-event-item {
    padding: 0.95rem 1rem;
    border-radius: 1rem;
    background: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.15);
}

.attendance-event-item__meta {
    margin-top: 0.6rem;
    color: #64748b;
    font-size: 0.84rem;
}

@media (max-width: 767.98px) {
    .attendance-worksite-drawer__body {
        padding: 1rem;
    }

    .attendance-worksite-drawer__header-title {
        font-size: 1.2rem;
    }

    .attendance-worksite-drawer__employee-toolbar {
        align-items: flex-start;
        flex-direction: column;
    }

    .attendance-worksite-drawer__employee-meta {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.35rem;
    }

    .attendance-worksite-drawer__count-chip {
        width: 100%;
        justify-content: center;
    }

    .attendance-worksite-drawer__detail-facts {
        grid-template-columns: 1fr;
    }

}
</style>
